import type { Skill } from '../content/types.ts';
import { contentRegistry } from '../content/dccCoreContent.ts';
import type { Character } from '../domain/character.ts';
import type { EditDraft } from './characterEditService.ts';
import type { CustomSkillDeps } from './customSkillService.ts';
import { createCustomSkill } from './customSkillService.ts';
import {
  buildCustomSkillDefinition,
  checkCustomSkillDraft,
} from '../rules/customSkills.ts';
import type { CustomSkillDraft, CustomSkillProblem } from '../rules/customSkills.ts';
import { applyChanges } from '../rules/characterChanges.ts';

export type CreateDefinitionForEditOutcome =
  | { ok: true; draft: EditDraft; skill: Skill; startingRank: number }
  | { ok: false; problems: CustomSkillProblem[] };

const PREVIEW_SKILL_ID = 'custom:e7c-starting-rank-preview';

/**
 * E7c's deliberate two-save boundary.
 *
 * The crawler-specific Starting Rank is validated FIRST, using the ordinary
 * Character change authority against a non-persistent preview Skill. Only once
 * that succeeds may the reusable definition be persisted through the canonical
 * E7b service. The crawler reference and Rank are then appended only to the
 * caller's in-memory EditDraft. No Character repository write occurs here; the
 * existing Edit Character Save remains the sole persistence boundary for
 * crawler state.
 */
export async function createCustomDefinitionForEditDraft(
  character: Character,
  editDraft: EditDraft,
  customDraft: CustomSkillDraft,
  startingRankRaw: string,
  deps: CustomSkillDeps,
): Promise<CreateDefinitionForEditOutcome> {
  const trimmedRank = startingRankRaw.trim();
  const startingRank = Number(trimmedRank);
  if (trimmedRank === '' || !Number.isInteger(startingRank) || startingRank < 1) {
    return {
      ok: false,
      problems: [{ field: 'id', message: 'Starting Rank must be a whole number of at least 1.' }],
    };
  }

  const definitionProblems = checkCustomSkillDraft(customDraft, contentRegistry.allSkills(), null);
  if (definitionProblems.length > 0) return { ok: false, problems: definitionProblems };

  const previewSkill = buildCustomSkillDefinition(
    customDraft,
    PREVIEW_SKILL_ID,
    '1970-01-01T00:00:00.000Z',
  ).skill;

  const probe: Character = {
    ...character,
    crawlerType: editDraft.crawlerType,
    size: editDraft.size,
    floor: editDraft.floor,
    skills: editDraft.skills.map((entry) => ({ ...entry })),
  };
  const rankCheck = applyChanges(
    probe,
    [
      {
        change: { kind: 'add-skill', skillId: PREVIEW_SKILL_ID, rank: startingRank },
        source: 'manual-edit',
        reason: 'Preview custom Skill starting Rank',
      },
    ],
    {
      now: '1970-01-01T00:00:00.000Z',
      lookupSkill: (id) =>
        id === PREVIEW_SKILL_ID ? previewSkill : contentRegistry.getSkill(id),
    },
  );
  if (!rankCheck.ok) {
    return {
      ok: false,
      problems: rankCheck.problems.map((problem) => ({ field: 'id', message: problem.message })),
    };
  }

  const loaded = await deps.customSkills.load();
  if (loaded.status === 'unreadable') {
    return {
      ok: false,
      problems: [
        {
          field: 'id',
          message:
            'The custom Skill library cannot be read, so nothing can be created or overwritten. ' +
            loaded.problems.join(' '),
        },
      ],
    };
  }

  const outcome = await createCustomSkill(loaded.library, customDraft, deps);
  if (!outcome.ok) return outcome;

  if (editDraft.skills.some((entry) => entry.skillId === outcome.skill.id)) {
    return {
      ok: false,
      problems: [{ field: 'id', message: 'That Skill is already in this crawler edit draft.' }],
    };
  }

  return {
    ok: true,
    skill: outcome.skill,
    startingRank,
    draft: {
      ...editDraft,
      skills: [...editDraft.skills, { skillId: outcome.skill.id, rank: startingRank }],
    },
  };
}
